import { X, ChevronLeft, ChevronRight, Play, Image as ImageIcon, Calendar, Clock, Tag, ExternalLink, Sparkles, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { FbAd } from '../../../../services/fb-ads'
import { isVideoUrl } from './media'
import { agentplatService } from '../../../../services/agentplat'
import { AD_ANALYZER_AGENT_ID, formatAdAnalyzerPrompt } from '../../../../config/ad-analyzer'
import ReactMarkdown from 'react-markdown'

interface AdDetailModalProps {
  ad: FbAd | null
  isOpen: boolean
  onClose: () => void
}

export function AdDetailModal({ ad, isOpen, onClose }: AdDetailModalProps) {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0)
  const [imageError, setImageError] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<string | null>(null)
  const [analysisError, setAnalysisError] = useState<string | null>(null)

  // Reset state when ad changes
  useEffect(() => {
    setCurrentMediaIndex(0)
    setImageError(false)
    setIsVideoPlaying(false)
    setAnalysisResult(null)
    setAnalysisError(null)
  }, [ad?.ad_id])

  // Handle analyze ad
  const handleAnalyzeAd = async () => {
    if (!ad?.ad_text && (!ad?.media_urls || ad.media_urls.length === 0)) {
      setAnalysisError('No ad content available to analyze')
      return
    }

    setIsAnalyzing(true)
    setAnalysisError(null)
    setAnalysisResult(null)

    try {
      // Build prompt - mention if we're including media
      const hasMedia = ad.media_urls && ad.media_urls.length > 0
      let prompt = formatAdAnalyzerPrompt(ad.ad_text || '(No text copy - analyze the visual creative only)')
      
      if (hasMedia) {
        prompt = prompt.replace(
          'And this is the script:',
          'I am also providing the ad creative (image/video) for visual analysis. Please analyze BOTH the script/copy AND the visual creative. And this is the script:'
        )
      }

      // Pass media URLs for visual analysis
      const result = await agentplatService.invokeAgent(
        AD_ANALYZER_AGENT_ID, 
        prompt,
        hasMedia ? ad.media_urls : undefined
      )
      setAnalysisResult(result.content)
    } catch (error) {
      console.error('Error analyzing ad:', error)
      setAnalysisError(error instanceof Error ? error.message : 'Failed to analyze ad')
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || !ad) return

      if (e.key === 'Escape') {
        onClose()
        return
      }

      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

      const mediaUrls = ad.media_urls || []
      if (mediaUrls.length <= 1) return

      setCurrentMediaIndex((prev) => {
        if (e.key === 'ArrowLeft') {
          return prev === 0 ? mediaUrls.length - 1 : prev - 1
        }
        return prev === mediaUrls.length - 1 ? 0 : prev + 1
      })
      setImageError(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, ad, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen || !ad) return null

  const mediaUrls = ad.media_urls || []
  const hasMultipleMedia = mediaUrls.length > 1
  const currentMedia = mediaUrls[currentMediaIndex]
  const currentIsVideo = isVideoUrl(currentMedia)
  const hasVideo = mediaUrls.some(isVideoUrl) || ad.media_type === 'video'
  const hasImage = mediaUrls.some((url) => !isVideoUrl(url))
  const computedMediaType = mediaUrls.length
    ? hasVideo && hasImage
      ? 'carousel'
      : hasVideo
        ? 'video'
        : 'image'
    : (ad.media_type || 'image')

  const navigatePrev = () => {
    if (hasMultipleMedia) {
      setCurrentMediaIndex((prev) => (prev === 0 ? mediaUrls.length - 1 : prev - 1))
      setImageError(false)
    }
  }

  const navigateNext = () => {
    if (hasMultipleMedia) {
      setCurrentMediaIndex((prev) => (prev === mediaUrls.length - 1 ? 0 : prev + 1))
      setImageError(false)
    }
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Unknown'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return dateStr
    }
  }

  const renderMedia = () => {
    if (!currentMedia || imageError) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-tf-bg-muted to-tf-bg-hover flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            {hasVideo ? (
              <Play className="w-16 h-16 text-tf-text-muted mx-auto mb-2" />
            ) : (
              <ImageIcon className="w-16 h-16 text-tf-text-muted mx-auto mb-2" />
            )}
            <p className="text-tf-text-muted text-sm">Media not available</p>
          </div>
        </div>
      )
    }

    if (currentIsVideo) {
      return (
        <div className="relative w-full min-h-[400px] bg-black flex items-center justify-center">
          <video
            src={currentMedia}
            controls
            autoPlay={isVideoPlaying}
            className="max-w-full max-h-[70vh] object-contain"
            onError={() => setImageError(true)}
            onPlay={() => setIsVideoPlaying(true)}
          />
        </div>
      )
    }

    return (
      <div className="relative w-full min-h-[400px] bg-tf-bg-muted flex items-center justify-center">
        <img
          src={currentMedia}
          alt={`${ad.page_name} - Image ${currentMediaIndex + 1}`}
          className="max-w-full max-h-[70vh] object-contain"
          onError={() => setImageError(true)}
        />
      </div>
    )
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative bg-tf-bg-card rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Media Section */}
        <div className="relative flex-1 bg-tf-bg-page flex items-center justify-center min-h-[300px] md:min-h-0">
          {renderMedia()}

          {/* Carousel Navigation */}
          {hasMultipleMedia && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); navigatePrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigateNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Dots indicator */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {mediaUrls.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); setCurrentMediaIndex(idx); setImageError(false); }}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentMediaIndex ? 'bg-tf-bg-card' : 'bg-tf-bg-card/50 hover:bg-tf-bg-card/75'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Details Section */}
        <div className="w-full md:w-96 p-6 overflow-y-auto max-h-[50vh] md:max-h-[90vh] bg-tf-bg-card">
          {/* Brand Header */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-tf-border">
            <div className="w-12 h-12 rounded-full bg-tf-feedback-info-bg flex items-center justify-center text-lg font-bold text-tf-feedback-info-text">
              {ad.page_name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <h2 className="font-semibold text-tf-text">{ad.page_name || 'Unknown Brand'}</h2>
              <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                ad.status === 'active' 
                  ? 'bg-tf-feedback-success-bg text-tf-feedback-success-text'
                  : 'bg-tf-bg-hover text-tf-text'
              }`}>
                {ad.status || 'Unknown'}
              </span>
            </div>
          </div>

          {/* Ad Text */}
          {ad.ad_text && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-tf-text-muted">Ad Copy</h3>
                <button
                  onClick={handleAnalyzeAd}
                  disabled={isAnalyzing}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-primary bg-primary/15 hover:bg-primary/20 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3" />
                      Analyze
                    </>
                  )}
                </button>
              </div>
              <p className="text-tf-text text-sm whitespace-pre-wrap leading-relaxed">
                {ad.ad_text}
              </p>
            </div>
          )}

          {/* Analysis Results */}
          {(analysisResult || analysisError) && (
            <div className="mb-4 p-4 bg-tf-bg-muted rounded-lg border border-tf-border">
              <h3 className="text-sm font-medium text-tf-text-sub mb-2 flex items-center gap-1">
                <Sparkles className="w-4 h-4 text-primary" />
                AI Analysis
              </h3>
              {analysisError ? (
                <p className="text-sm text-tf-feedback-error-text">{analysisError}</p>
              ) : (
                <div className="text-sm text-tf-text-sub prose prose-sm max-w-none">
                  <ReactMarkdown>{analysisResult || ''}</ReactMarkdown>
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="space-y-3 mb-4">
            {/* Media Type */}
            <div className="flex items-center gap-2 text-sm text-tf-text-muted">
              {computedMediaType === 'video' ? <Play className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
              <span className="capitalize">{computedMediaType}</span>
              {hasMultipleMedia && (
                <span className="text-tf-text-muted">
                  ({currentMediaIndex + 1} of {mediaUrls.length})
                </span>
              )}
            </div>

            {/* Created Date */}
            {ad.created_at && (
              <div className="flex items-center gap-2 text-sm text-tf-text-muted">
                <Calendar className="w-4 h-4" />
                <span>Created: {formatDate(ad.created_at)}</span>
              </div>
            )}

            {/* Scraped Date */}
            {ad.scraped_at && (
              <div className="flex items-center gap-2 text-sm text-tf-text-muted">
                <Clock className="w-4 h-4" />
                <span>Scraped: {formatDate(ad.scraped_at)}</span>
              </div>
            )}
          </div>

          {/* Platforms */}
          {ad.platforms && ad.platforms.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-tf-text-muted mb-2 flex items-center gap-1">
                <Tag className="w-4 h-4" />
                Platforms
              </h3>
              <div className="flex flex-wrap gap-2">
                {ad.platforms.map((platform) => (
                  <span
                    key={platform}
                    className="px-3 py-1 bg-tf-feedback-info-bg text-tf-feedback-info-text rounded-full text-xs font-medium capitalize"
                  >
                    {platform}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Ad ID for reference */}
          <div className="mt-6 pt-4 border-t border-tf-border">
            <div className="flex items-center justify-between text-xs text-tf-text-muted">
              <span>Ad ID: {ad.ad_id}</span>
              <a
                href={`https://www.facebook.com/ads/library/?id=${ad.ad_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-primary hover:text-primary-dark transition-colors"
              >
                View on Meta <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdDetailModal
